import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AcademicCapIcon } from '@heroicons/react/24/outline'
import type { PublicCourse } from '@/types/course'
import { CourseApi } from '@/services/courseApi'
import { findCoursesByCodes, courseLink } from '@/config/careerPaths'
import { useAuthStore } from '@/store/authStore'
import { useRedirect } from '@/hooks/useRedirect'
import CourseRegistrationForm from '@/components/registration/CourseRegistrationForm'

/**
 * Banner đăng ký AI4BA trên thư viện Skill — mở cùng MH đăng ký như trang chi tiết khóa học.
 */
const Ai4baEnrollBanner = () => {
  const { isAuthenticated } = useAuthStore()
  const { redirectToLogin } = useRedirect()
  const [course, setCourse] = useState<PublicCourse | null>(null)
  const [loading, setLoading] = useState(true)
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        // size tối đa backend cho phép là 50 → duyệt trang tới khi thấy AI4BA
        let page = 1
        let found: PublicCourse | null = null
        for (;;) {
          const res = await CourseApi.getCourses({ limit: 50, page })
          found = findCoursesByCodes(res.courses || [], ['AI4BA'])[0] || null
          if (found || !res.pagination.hasNextPage || page >= 10) break
          page += 1
        }
        if (!cancelled) setCourse(found)
      } catch {
        if (!cancelled) setCourse(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleRegister = () => {
    if (!course) return
    if (!isAuthenticated) {
      redirectToLogin()
      return
    }
    setShowRegistrationForm(true)
  }

  if (loading) return null

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-m2-surface-container p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <AcademicCapIcon className="mt-0.5 h-7 w-7 shrink-0 text-amber-700" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-m2-on-surface">Khóa học AI4BA</p>
            <p className="mt-0.5 text-xs text-m2-on-surface-variant leading-relaxed sm:text-sm">
              Học cách dùng bộ skill BA với AI agent — đăng ký ngay để theo dõi và thực hành trên khóa
              học.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
          <Link
            to={course ? courseLink(course) : '/courses?search=AI4BA'}
            className="inline-flex items-center justify-center rounded-md border border-m2-outline px-3 py-2 text-sm font-medium text-m2-on-surface hover:bg-m2-surface-high"
          >
            Xem chi tiết
          </Link>
          {course ? (
            <button
              type="button"
              onClick={handleRegister}
              className="inline-flex items-center justify-center rounded-md bg-m2-primary px-4 py-2 text-sm font-semibold text-m2-on-primary hover:bg-eh-forest-shadow"
            >
              Đăng ký khóa học AI4BA
            </button>
          ) : (
            <Link
              to="/courses?search=AI4BA"
              className="inline-flex items-center justify-center rounded-md bg-m2-primary px-4 py-2 text-sm font-semibold text-m2-on-primary hover:bg-eh-forest-shadow"
            >
              Đăng ký khóa học AI4BA
            </Link>
          )}
        </div>
      </div>

      {showRegistrationForm && course && (
        <CourseRegistrationForm
          course={course}
          onClose={() => setShowRegistrationForm(false)}
        />
      )}
    </>
  )
}

export default Ai4baEnrollBanner
